import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { DIMENSIONALITY_VALUES } from 'constants/businessTool';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';
import { debounce } from 'lodash';
import { useGetCashReceivedFromCustomerCalculation } from 'services/calculations/cashFlow';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { subtractPeriodValueBaseOnPreviousPeriod } from 'components/BusinessToolForm/utils/helpers';

import ToastService from 'helpers/ToastService';

import { IFormContentTypes, observedFields } from '../../utils/form';

export const useCashReceivedFromCustomers = () => {
  const { id: businessPlanId = '' } = useParams();

  const { setValue } = useFormContext();

  const getCashReceivedFromCustomerCalculation = useGetCashReceivedFromCustomerCalculation();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MARKETING_AND_SALES,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKETING_AND_SALES.SALES_PLAN,
  );

  const [cashReceivedFromCustomersFiled, accountsReceivableFiled, changesInAccountsReceivableFiled] =
    observedFields.cashFlowFromCustomers.setCalculatedValueFieldNames;

  const debouncedUpdateFunction = useMemo(
    () =>
      debounce(async (cashReceivingData, unit) => {
        const params = {
          unit,
          businessPlanId,
          cashReceivingData,
        };

        try {
          const { cashReceivedFromCustomer, accountsReceivable } =
            await getCashReceivedFromCustomerCalculation.mutateAsync(params);

          const changesInAccountsReceivable = subtractPeriodValueBaseOnPreviousPeriod(accountsReceivable, cashReceivingData.initAccountReceivable);
          setValue(cashReceivedFromCustomersFiled, cashReceivedFromCustomer);
          setValue(accountsReceivableFiled, accountsReceivable);
          setValue(changesInAccountsReceivableFiled, changesInAccountsReceivable);
        } catch (error) {
          if (error instanceof Error) ToastService.showError(error.message);
        }
      }, 500),

    [],
  );

  const cashReceivedFromCustomersCalculationHandler = useCallback(
    async (content: IFormContentTypes['cashFlowFromCustomers']) => {
      if (!canAccess) return;

      const [operationUnit, creditSalesDays, creditSalesPercent, initAccountReceivable] = content;
      const creditSalesDaysNumber = parseInt(creditSalesDays);

      const cashReceivingData = {
        daysToGetPaid: creditSalesDaysNumber || 0,
        salesOnCreditPercent: creditSalesPercent,
        initAccountReceivable
      };
      const unit = operationUnit ?? DIMENSIONALITY_VALUES.SINGLE;

      debouncedUpdateFunction(cashReceivingData, unit);
    },

    [debouncedUpdateFunction],
  );

  return cashReceivedFromCustomersCalculationHandler;
};
